import { useSupabase } from '@/composables/useSupabase'
import type { PrincipalType } from '@/types/authorization'
import { DB_ROLE_TO_PRINCIPAL, PRINCIPAL_TO_DB_ROLE, ROLE_ROUTE_ACCESS } from '@/types/authorization'
import { jwtDecode } from 'jwt-decode'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

interface JwtPayload {
    user_role?: string
    [key: string]: unknown
}

export const useAuthorizationStore = defineStore('authorization', () => {
    const { session } = useSupabase()

    // Role state — starts null, populated from JWT or DB
    const role = ref<PrincipalType | null>(null)
    const principalAttributes = ref<Record<string, string>>({})

    const isRoleAssigned = computed(() => role.value !== null)

    // -----------------------------------------------------------------------
    // Read role from JWT claims (set by the Auth Hook reading app.user_roles)
    // -----------------------------------------------------------------------
    function syncRoleFromJwt(): PrincipalType | null {
        try {
            const accessToken = session.value?.access_token
            if (!accessToken) return null

            const decoded = jwtDecode<JwtPayload>(accessToken)
            const dbRole = decoded.user_role

            if (dbRole && DB_ROLE_TO_PRINCIPAL[dbRole]) {
                return DB_ROLE_TO_PRINCIPAL[dbRole]
            }
        } catch {
            // JWT decode failed
        }
        return null
    }

    // Watch for session changes and sync role from JWT
    watch(session, () => {
        const jwtRole = syncRoleFromJwt()
        if (jwtRole) {
            role.value = jwtRole
        }
    }, { immediate: true })

    // -----------------------------------------------------------------------
    // Fetch role from DB (authoritative source of truth)
    // Call this when the JWT might not have the role yet (e.g. first login
    // before the Auth Hook has injected the claim).
    // -----------------------------------------------------------------------
    async function fetchRole(): Promise<PrincipalType | null> {
        try {
            const { supabase } = await import('@/lib/supabase')
            const { data, error } = await supabase.rpc('get_my_role')
            if (error) throw error

            if (data && DB_ROLE_TO_PRINCIPAL[data]) {
                const r = DB_ROLE_TO_PRINCIPAL[data]
                role.value = r
                return r
            }
            return null
        } catch (err) {
            if (import.meta.env.DEV) {
                console.error('[authzStore] Failed to fetch role from DB:', err)
            }
            return null
        }
    }

    // -----------------------------------------------------------------------
    // Set role — writes to DB first, then syncs in-memory state.
    // This is a CRITICAL operation; if the DB write fails, the role is NOT
    // set locally to avoid desync between client and server.
    // -----------------------------------------------------------------------
    async function setRole(newRole: PrincipalType, attributes?: Record<string, string>) {
        const { supabase } = await import('@/lib/supabase')
        const dbRole = PRINCIPAL_TO_DB_ROLE[newRole]
        const { error } = await supabase.rpc('set_my_role', { new_role: dbRole })

        if (error) {
            console.error('[authzStore] Failed to persist role to DB:', error)
            throw new Error(`Failed to set role: ${error.message}`)
        }

        // DB write succeeded — update in-memory state
        role.value = newRole
        if (attributes) {
            principalAttributes.value = attributes
        }
    }

    /**
     * Sync role in-memory WITHOUT writing to DB.
     * Use when the role was already read from JWT or DB.
     */
    function syncRole(existingRole: PrincipalType) {
        role.value = existingRole
    }

    function clearRole() {
        role.value = null
        principalAttributes.value = {}
    }

    /**
     * Check if the current role has access to a given route.
     */
    function checkRouteAccess(route: string): boolean {
        if (!role.value) return false
        const allowedPrefixes = ROLE_ROUTE_ACCESS[role.value]
        if (!allowedPrefixes) return false
        return allowedPrefixes.some((prefix) => route.startsWith(prefix))
    }

    return {
        role,
        principalAttributes,
        isRoleAssigned,
        setRole,
        syncRole,
        fetchRole,
        clearRole,
        checkRouteAccess,
        syncRoleFromJwt,
    }
})
